import { z } from "zod";
import { ImageSet } from "./image";
import { Timestamp, UserId } from "./primitives";
import { Username } from "./username";

const EXAMPLE_FOLDER = "https://paxusercontent.co/profiles/0199f0be-4d1a-7b55-8c71-59a0d2e6c884";

const EXAMPLE = {
  profileId: "0199f0be-4d1a-7b55-8c71-59a0d2e6c884",
  username: "Grace_Hopper",
  displayName: "Grace Hopper",
  avatar: {
    sources: [64, 128, 256, 512].map(size => ({
      url: `${EXAMPLE_FOLDER}/avatar/k3v9x2mq7tfa/${size}.avif`,
      width: size,
      height: size,
    })),
  },
  banner: {
    sources: [750, 1500, 3000].map(width => ({
      url: `${EXAMPLE_FOLDER}/banner/w4n8c2r6y5ph/${width}.avif`,
      width,
      height: width / 3,
    })),
  },
  bio: "Computer pioneer and naval officer.",
  region: "New York, United States",
  friendCount: 12,
  publishedPresetCount: 3,
  lastActiveAt: "2026-09-18T20:12:00.000Z",
  createdAt: "2026-09-04T17:00:00.000Z",
  updatedAt: "2026-09-18T20:10:00.000Z",
} as const;

export const ProfileId = UserId.meta({
  id: "ProfileId",
  description:
    "A public profile's stable id. It is the same UUID as the owning Better Auth user id, so a guest who connects an account keeps it.",
  example: EXAMPLE.profileId,
});
export type ProfileId = z.infer<typeof ProfileId>;

export const ProfileSummary = z
  .strictObject({
    profileId: ProfileId,
    username: Username.nullable().meta({
      description: "The profile's claimed username, or null before the owner claims one.",
      example: EXAMPLE.username,
    }),
    displayName: z.string().min(1).max(50).meta({
      description: "The profile's public display name.",
      example: EXAMPLE.displayName,
    }),
    avatar: ImageSet.nullable().meta({
      description: "The profile's avatar, a square image, or null when none is set.",
      example: EXAMPLE.avatar,
    }),
  })
  .meta({
    id: "ProfileSummary",
    description: "The fields needed to identify and link to one public profile.",
    example: {
      profileId: EXAMPLE.profileId,
      username: EXAMPLE.username,
      displayName: EXAMPLE.displayName,
      avatar: EXAMPLE.avatar,
    },
  });
export type ProfileSummary = z.infer<typeof ProfileSummary>;

export const PublicProfile = ProfileSummary.extend({
  banner: ImageSet.nullable().meta({
    description:
      "The profile's banner, three times as wide as it is tall, or null when none is set.",
    example: EXAMPLE.banner,
  }),
  bio: z.string().max(500).nullable().meta({
    description: "The profile's public biography, or null when none is set.",
    example: EXAMPLE.bio,
  }),
  region: z.string().max(100).nullable().meta({
    description: "Public location text supplied by the profile owner, or null when none is set.",
    example: EXAMPLE.region,
  }),
  friendCount: z.number().int().nonnegative().meta({
    description: "How many accepted friendships this profile is part of. Public on every profile.",
    example: EXAMPLE.friendCount,
  }),
  publishedPresetCount: z.number().int().nonnegative().meta({
    description: "How many presets this profile owns that have a published version.",
    example: EXAMPLE.publishedPresetCount,
  }),
  lastActiveAt: Timestamp.nullable().meta({
    description:
      "The latest recorded authenticated activity, or null before any activity is recorded.",
    example: EXAMPLE.lastActiveAt,
  }),
  createdAt: Timestamp.meta({
    description: "When the profile was created.",
    example: EXAMPLE.createdAt,
  }),
  updatedAt: Timestamp.meta({
    description: "When a public profile field last changed.",
    example: EXAMPLE.updatedAt,
  }),
}).meta({
  id: "PublicProfile",
  description: "Every public field on one registered account's profile.",
  example: EXAMPLE,
});
export type PublicProfile = z.infer<typeof PublicProfile>;
